import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, ArrowLeft } from 'lucide-react';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';
import BookingLayoutDesigner from './BookingLayoutDesigner';

interface AddNewEventProps {
  onClose: () => void;
  onEventAdded?: () => void;
  organizerId?: number;
}

export default function AddNewEvent({ onClose, onEventAdded, organizerId }: AddNewEventProps) {
  const [eventType, setEventType] = useState<'movie' | ''>('')
  // Movie form data
  const [movieData, setMovieData] = useState({
    title: '',
    image_url: '',
    description: '',
    duration: 120,
    price: 10,
  });

  // Screening form data
  const [screeningData, setScreeningData] = useState({
    start_time: '',
    location: '',
    available_seats: 100,
    enable_seatsplitting: true,
    silver_percentage: 60,
    gold_percentage: 30,
    diamond_percentage: 10,
    seat_numbering_start: 'top-left',
    // Per-tier pricing — 0 means "inherit movie base price" (backward compat)
    silver_price: 0,
    gold_price: 0,
    diamond_price: 0,
  });

  const [loading, setLoading] = useState(false);
  const [showDesigner, setShowDesigner] = useState(false);
  const [layoutDraft, setLayoutDraft] = useState<{ name: string; rows: number; cols: number; seats: boolean[][] } | null>(null);
  const layoutCapacity = layoutDraft ? layoutDraft.seats.reduce((acc, row) => acc + row.filter(Boolean).length, 0) : 0;

  const [creationMode, setCreationMode] = useState<'new' | 'existing'>('new');
  const [existingMovies, setExistingMovies] = useState<{ id: number; title: string }[]>([]);
  const [selectedMovieId, setSelectedMovieId] = useState<string>('');

  useEffect(() => {
    if (organizerId) {
      supabase.from('movies')
        .select('id, title')
        .eq('organizer_id', organizerId)
        .order('title')
        .then(({ data }) => {
          if (data) setExistingMovies(data);
        });
    }
  }, [organizerId]);

  const handleMovieChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    setMovieData(prev => ({
      ...prev,
      [name]: type === 'number' ? Number(value) : value,
    }));
  };

  const handleScreeningChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setScreeningData(prev => ({
      ...prev,
      [name]: type === 'number' ? Number(value) : value
    }));
  };

  const handleSeatSplittingToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (layoutDraft) return;
    setScreeningData(prev => ({ ...prev, enable_seatsplitting: e.target.checked }));
  };



  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate movie data only if creating new
    if (creationMode === 'new') {
      if (!movieData.title || !movieData.image_url || !movieData.description) {
        toast.error('Please fill in all movie fields');
        return;
      }
      if (movieData.duration <= 0) {
        toast.error('Duration must be greater than 0');
        return;
      }
      if (movieData.price <= 0) {
        toast.error('Price must be greater than 0');
        return;
      }
    } else {
      if (!selectedMovieId) {
        toast.error('Please select an existing movie');
        return;
      }
    }

    // Validate screening data
    if (!screeningData.start_time || !screeningData.location) {
      toast.error('Please fill in all screening fields');
      return;
    }

    if (screeningData.enable_seatsplitting) {
      if (screeningData.silver_percentage + screeningData.gold_percentage + screeningData.diamond_percentage !== 100) {
        toast.error('Seat percentages must total 100%');
        return;
      }
    }

    setLoading(true);

    try {
      // Use organizer ID from props
      const organizer_id = organizerId;
      console.log('Using organizer ID from props:', organizer_id);

      if (!organizer_id) {
        toast.error('Could not determine organizer. Please ensure you are logged in as an organizer or editor.');
        setLoading(false);
        return;
      }

      let movieId: number;

      if (creationMode === 'new') {
        // Step 1: Insert movie
        const { data: movieResult, error: movieError } = await supabase
          .from('movies')
          .insert([
            {
              title: movieData.title,
              image_url: movieData.image_url,
              description: movieData.description,
              duration: movieData.duration,
              price: movieData.price,
              organizer_id: organizer_id,
            },
          ])
          .select();

        if (movieError) throw movieError;
        if (!movieResult || movieResult.length === 0) throw new Error('Failed to create movie');
        movieId = movieResult[0].id;
      } else {
        movieId = Number(selectedMovieId);
      }

      // Step 2: Insert screening
      const availableSeatsToUse = layoutDraft ? layoutCapacity : screeningData.available_seats;
      const enableSplitToUse = layoutDraft ? false : screeningData.enable_seatsplitting;
      const { data: screeningResult, error: screeningError } = await supabase
        .from('screenings')
        .insert([
          {
            movie_id: movieId,
            start_time: new Date(screeningData.start_time).toISOString(),
            location: screeningData.location,
            available_seats: availableSeatsToUse,
            salt: 'default_salt',
            enable_seatsplitting: enableSplitToUse,
            seat_numbering_start: screeningData.seat_numbering_start,
          }
        ])
        .select();

      if (screeningError) throw screeningError;
      if (!screeningResult || screeningResult.length === 0) throw new Error('Failed to create screening');

      const screeningId = screeningResult[0].id;

      // Step 3: Insert seat splitting if enabled
      if (enableSplitToUse) {
        let silverTotal = availableSeatsToUse;
        let goldTotal = 0;
        let diamondTotal = 0;

        silverTotal = Math.round(availableSeatsToUse * (screeningData.silver_percentage / 100));
        goldTotal = Math.round(availableSeatsToUse * (screeningData.gold_percentage / 100));
        diamondTotal = availableSeatsToUse - silverTotal - goldTotal;

        const { error: seatsError } = await supabase
          .from('seatssplitting')
          .insert([
            {
              screening_id: screeningId,
              silver_total: silverTotal,
              silver_booked: 0,
              gold_total: goldTotal,
              gold_booked: 0,
              diamond_total: diamondTotal,
              diamond_booked: 0,
              // Only persist explicit prices; NULL = fall back to movie.price in BookingModal
              silver_price: screeningData.silver_price > 0 ? screeningData.silver_price : null,
              gold_price:   screeningData.gold_price   > 0 ? screeningData.gold_price   : null,
              diamond_price: screeningData.diamond_price > 0 ? screeningData.diamond_price : null,
            }
          ]);

        if (seatsError) throw seatsError;
      }

      // Step 4: Insert seat layout if designed
      if (layoutDraft) {
        const { error: layoutErr } = await supabase
          .from('seat_layouts')
          .upsert({
            screening_id: screeningId,
            layout_name: layoutDraft.name,
            rows: layoutDraft.rows,
            cols: layoutDraft.cols,
            seats: layoutDraft.seats,
          }, { onConflict: 'screening_id' });

        if (layoutErr) throw layoutErr;
      }

      toast.success('Event created successfully!');
      onEventAdded?.();
      onClose();
    } catch (error) {
      console.error('Error creating event:', error);
      toast.error('Failed to create event');
    } finally {
      setLoading(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 bg-black/60 z-[100] flex items-center justify-center p-4">
      <div className="bg-gray-800 rounded-lg shadow-2xl w-full max-w-[95vw] md:max-w-4xl mx-4 max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-gray-800 border-b border-gray-700 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-200 transition-colors"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <h2 className="text-2xl font-bold text-white">Create New Event</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-200 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-8">
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-white border-b border-gray-700 pb-2">Event Type</h3>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Select Type *</label>
              <select
                value={eventType}
                onChange={e => setEventType(e.target.value as 'movie')}
                className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
              >
                <option value="" disabled>Select an event type</option>
                <option value="movie">Movie</option>
              </select>
            </div>
          </div>

          {eventType === 'movie' && (
            <>
              <div className="space-y-4">
                <div className="flex justify-between items-center border-b border-gray-700 pb-2">
                  <h3 className="text-lg font-semibold text-white">Movie Information</h3>
                  <div className="flex bg-gray-700 rounded-lg p-1 gap-1">
                    <button
                      type="button"
                      onClick={() => setCreationMode('new')}
                      className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${creationMode === 'new' ? 'bg-indigo-600 text-white' : 'text-gray-400 hover:text-white'}`}
                    >
                      Create New
                    </button>
                    <button
                      type="button"
                      onClick={() => setCreationMode('existing')}
                      className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${creationMode === 'existing' ? 'bg-indigo-600 text-white' : 'text-gray-400 hover:text-white'}`}
                    >
                      Select Existing
                    </button>
                  </div>
                </div>

                {creationMode === 'existing' ? (
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Select Movie *</label>
                    <select
                      value={selectedMovieId}
                      onChange={e => setSelectedMovieId(e.target.value)}
                      className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                    >
                      <option value="">Select a movie...</option>
                      {existingMovies.map(m => (
                        <option key={m.id} value={m.id}>{m.title}</option>
                      ))}
                    </select>
                  </div>
                ) : (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Title *</label>
                      <input
                        type="text"
                        name="title"
                        value={movieData.title}
                        onChange={handleMovieChange}
                        className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-indigo-500"
                        placeholder="Enter movie title"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Image URL *</label>
                      <input
                        type="text"
                        name="image_url"
                        value={movieData.image_url}
                        onChange={handleMovieChange}
                        placeholder="https://..."
                        className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-indigo-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Description *</label>
                      <textarea
                        name="description"
                        value={movieData.description}
                        onChange={handleMovieChange}
                        rows={3}
                        className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-indigo-500"
                        placeholder="Enter movie description"
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Duration (min) *</label>
                        <input
                          type="number"
                          name="duration"
                          value={movieData.duration}
                          onChange={handleMovieChange}
                          min={1}
                          className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-indigo-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Price (€) *</label>
                        <input
                          type="number"
                          name="price"
                          value={movieData.price}
                          onChange={handleMovieChange}
                          step="0.01"
                          min={0.01}
                          className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-indigo-500"
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>


              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-white border-b border-gray-700 pb-2">Screening Details</h3>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Start Time *</label>
                  <input
                    type="datetime-local"
                    name="start_time"
                    value={screeningData.start_time}
                    onChange={handleScreeningChange}
                    className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-indigo-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Location *</label>
                  <input
                    type="text"
                    name="location"
                    value={screeningData.location}
                    onChange={handleScreeningChange}
                    placeholder="e.g., Cinema Hall A, Mumbai"
                    className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-indigo-500"
                  />
                </div>

              </div>


              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-white border-b border-gray-700 pb-2">Seat Configuration</h3>



                <div>
                  <label className="inline-flex items-center gap-2 text-sm font-medium text-gray-300">
                    <input
                      type="checkbox"
                      name="enable_seatsplitting"
                      checked={screeningData.enable_seatsplitting}
                      onChange={handleSeatSplittingToggle}
                      className="rounded border-gray-600 bg-gray-700"
                    />
                    Enable seat splitting (Silver, Gold, Diamond tiers)
                  </label>
                </div>

                {(screeningData.enable_seatsplitting || layoutDraft) && (
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Total Available Seats</label>
                    {layoutDraft ? (
                      <input
                        type="number"
                        name="available_seats"
                        value={layoutCapacity}
                        disabled
                        className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-gray-400 cursor-not-allowed"
                      />
                    ) : (
                      <input
                        type="number"
                        name="available_seats"
                        value={screeningData.available_seats}
                        onChange={handleScreeningChange}
                        min="1"
                        className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-indigo-500"
                      />
                    )}
                  </div>
                )}

                <div>
                  <button
                    type="button"
                    onClick={() => setShowDesigner(true)}
                    className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 transition-colors"
                  >
                    Design Seat Layout
                  </button>
                  {layoutDraft && (
                    <p className="mt-2 text-xs text-gray-400">Layout saved: {layoutDraft.name} ({layoutDraft.rows}x{layoutDraft.cols})</p>
                  )}
                </div>

                {screeningData.enable_seatsplitting && !layoutDraft && (
                  <div className="bg-gray-700/50 rounded-lg p-4 space-y-4">
                    <h3 className="text-sm font-semibold text-gray-300">Seat Allocation</h3>

                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Silver Seats: {screeningData.silver_percentage}%
                      </label>
                      <input
                        type="range"
                        name="silver_percentage"
                        value={screeningData.silver_percentage}
                        onChange={handleScreeningChange}
                        min="0"
                        max="100"
                        className="w-full h-2 bg-gray-600 rounded-lg"
                      />
                      <p className="text-xs text-gray-400 mt-1">
                        ({Math.round(screeningData.available_seats * (screeningData.silver_percentage / 100))} seats)
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Gold Seats: {screeningData.gold_percentage}%
                      </label>
                      <input
                        type="range"
                        name="gold_percentage"
                        value={screeningData.gold_percentage}
                        onChange={handleScreeningChange}
                        min="0"
                        max="100"
                        className="w-full h-2 bg-gray-600 rounded-lg"
                      />
                      <p className="text-xs text-gray-400 mt-1">
                        ({Math.round(screeningData.available_seats * (screeningData.gold_percentage / 100))} seats)
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Diamond Seats: {screeningData.diamond_percentage}%
                      </label>
                      <input
                        type="range"
                        name="diamond_percentage"
                        value={screeningData.diamond_percentage}
                        onChange={handleScreeningChange}
                        min="0"
                        max="100"
                        className="w-full h-2 bg-gray-600 rounded-lg"
                      />
                      <p className="text-xs text-gray-400 mt-1">
                        ({Math.round(screeningData.available_seats * (screeningData.diamond_percentage / 100))} seats)
                      </p>
                    </div>

                    {screeningData.silver_percentage + screeningData.gold_percentage + screeningData.diamond_percentage !== 100 && (
                      <p className="text-sm text-red-400">
                        Total: {screeningData.silver_percentage + screeningData.gold_percentage + screeningData.diamond_percentage}% (must be 100%)
                      </p>
                    )}

                    {/* Per-tier pricing — optional, falls back to movie base price if left blank */}
                    <div className="border-t border-gray-600 pt-4">
                      <h4 className="text-sm font-semibold text-gray-300 mb-3">
                        Tier Pricing
                        <span className="ml-2 text-xs font-normal text-gray-500">
                          (leave 0 to use movie base price €{movieData.price})
                        </span>
                      </h4>
                      <div className="grid grid-cols-3 gap-3">
                        {(['silver', 'gold', 'diamond'] as const).map(tier => (
                          <div key={tier}>
                            <label className="block text-xs font-medium text-gray-400 mb-1 capitalize">
                              {tier} (€)
                            </label>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={screeningData[`${tier}_price` as 'silver_price' | 'gold_price' | 'diamond_price'] || ''}
                              onChange={e =>
                                setScreeningData(prev => ({
                                  ...prev,
                                  [`${tier}_price`]: e.target.value === '' ? 0 : Number(e.target.value),
                                }))
                              }
                              placeholder={`€${movieData.price}`}
                              className="w-full px-3 py-1.5 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:border-indigo-500"
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>


              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 px-4 py-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium disabled:opacity-50"
                >
                  {loading ? 'Creating...' : 'Create Event'}
                </button>
              </div>
            </>
          )}
        </form>
      </div>


      <BookingLayoutDesigner
        show={showDesigner}
        initialLayout={layoutDraft ? { rows: layoutDraft.rows, cols: layoutDraft.cols, seats: layoutDraft.seats } : null}
        initialName={layoutDraft?.name || ''}
        initialSeatNumberingStart={screeningData.seat_numbering_start}
        onClose={() => setShowDesigner(false)}
        onSeatNumberingStartChange={(value) => {
          setScreeningData(prev => ({ ...prev, seat_numbering_start: value as any }));
        }}
        onSave={(name, layout, seatNumberingStart) => {
          setLayoutDraft({ name, rows: layout.rows, cols: layout.cols, seats: layout.seats });
          setScreeningData(prev => ({ ...prev, seat_numbering_start: seatNumberingStart as any }));
        }}
      />
    </div>,
    document.body
  );
}
